import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Modal } from '../common/Modal'
import { Input } from '../common/Input'
import { Select } from '../common/Select'
import { Button } from '../common/Button'
import { TimePicker } from '../common/TimePicker'
import { useToast } from '../common/Toast'
import { ScheduleBuilder } from './ScheduleBuilder'

interface MedicationData {
  _id: Id<'medications'>
  name: string
  timeSlot: string
  specificTime: string
  notes?: string
  frequency?: string
  frequencyDayOfWeek?: number
  frequencyOrdinal?: string
  frequencyDayOfMonth?: number
  oneTimeDate?: string
  remindDayBefore?: boolean
}

interface MedicationFormModalProps {
  isOpen: boolean
  onClose: () => void
  lovedOneId: Id<'lovedOnes'>
  medication?: MedicationData
}

export function MedicationFormModal({
  isOpen,
  onClose,
  lovedOneId,
  medication,
}: MedicationFormModalProps) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const createMedication = useMutation(api.medications.create)
  const updateMedication = useMutation(api.medications.update)

  const isEditMode = !!medication

  const [name, setName] = useState('')
  const [timeSlot, setTimeSlot] = useState('morning')
  const [specificTime, setSpecificTime] = useState('')
  const [notes, setNotes] = useState('')
  const [frequency, setFrequency] = useState('daily')
  const [frequencyDayOfWeek, setFrequencyDayOfWeek] = useState<number | undefined>()
  const [frequencyOrdinal, setFrequencyOrdinal] = useState<string | undefined>()
  const [frequencyDayOfMonth, setFrequencyDayOfMonth] = useState<number | undefined>()
  const [oneTimeDate, setOneTimeDate] = useState<string | undefined>()
  const [remindDayBefore, setRemindDayBefore] = useState(false)

  const [nameError, setNameError] = useState('')
  const [timeSlotError, setTimeSlotError] = useState('')
  const [specificTimeError, setSpecificTimeError] = useState('')
  const [generalError, setGeneralError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when modal opens or medication changes
  useEffect(() => {
    if (isOpen) {
      if (medication) {
        setName(medication.name)
        setTimeSlot(medication.timeSlot)
        setSpecificTime(medication.specificTime)
        setNotes(medication.notes || '')
        setFrequency(medication.frequency || 'daily')
        setFrequencyDayOfWeek(medication.frequencyDayOfWeek)
        setFrequencyOrdinal(medication.frequencyOrdinal)
        setFrequencyDayOfMonth(medication.frequencyDayOfMonth)
        setOneTimeDate(medication.oneTimeDate)
        setRemindDayBefore(medication.remindDayBefore ?? false)
      } else {
        setName('')
        setTimeSlot('morning')
        setSpecificTime('')
        setNotes('')
        setFrequency('daily')
        setFrequencyDayOfWeek(undefined)
        setFrequencyOrdinal(undefined)
        setFrequencyDayOfMonth(undefined)
        setOneTimeDate(undefined)
        setRemindDayBefore(false)
      }
      setNameError('')
      setTimeSlotError('')
      setSpecificTimeError('')
      setGeneralError('')
    }
  }, [isOpen, medication])

  const timeSlotOptions = [
    { value: 'morning', label: t('medicationManagement.timeSlots.morning') },
    { value: 'afternoon', label: t('medicationManagement.timeSlots.afternoon') },
    { value: 'evening', label: t('medicationManagement.timeSlots.evening') },
    { value: 'bedtime', label: t('medicationManagement.timeSlots.bedtime') },
  ]

  const validate = (): boolean => {
    let valid = true

    if (!name.trim()) {
      setNameError(t('medicationManagement.validation.nameRequired'))
      valid = false
    } else if (name.trim().length > 100) {
      setNameError(t('medicationManagement.validation.nameTooLong'))
      valid = false
    }

    if (!timeSlot) {
      setTimeSlotError(t('medicationManagement.validation.timeSlotRequired'))
      valid = false
    }

    if (!specificTime.trim()) {
      setSpecificTimeError(t('medicationManagement.validation.specificTimeRequired'))
      valid = false
    } else {
      const timeRegex = /^(1[0-2]|0?[1-9]):[0-5][0-9]\s?(AM|PM)$/i
      if (!timeRegex.test(specificTime.trim())) {
        setSpecificTimeError(t('medicationManagement.validation.invalidTimeFormat'))
        valid = false
      }
    }

    if (frequency === 'one_time' && !oneTimeDate) {
      setGeneralError(t('medicationManagement.validation.dateRequired'))
      valid = false
    }

    return valid
  }

  const handleSubmit = async () => {
    setGeneralError('')
    if (!validate()) return

    setIsSubmitting(true)

    try {
      if (isEditMode && medication) {
        const result = await updateMedication({
          medicationId: medication._id,
          name: name.trim(),
          timeSlot: timeSlot as 'morning' | 'afternoon' | 'evening' | 'bedtime',
          specificTime: specificTime.trim(),
          notes: notes.trim() || undefined,
          frequency: frequency as
            | 'daily'
            | 'weekly'
            | 'biweekly'
            | 'monthly_day_of_week'
            | 'monthly_date'
            | 'one_time',
          frequencyDayOfWeek,
          frequencyOrdinal: frequencyOrdinal as '1st' | '2nd' | '3rd' | '4th' | 'last' | undefined,
          frequencyDayOfMonth,
          oneTimeDate: frequency === 'one_time' ? oneTimeDate : undefined,
          remindDayBefore: frequency === 'one_time' ? remindDayBefore : undefined,
        })
        if (!result.success) {
          if (result.error === 'Duplicate medication') {
            setGeneralError(t('medicationManagement.validation.duplicateMedication'))
          } else {
            setGeneralError(result.error || t('errors.generic'))
          }
          return
        }
        showToast({
          message: t('medicationManagement.toast.medicationUpdated', { name: name.trim() }),
          type: 'success',
        })
      } else {
        const result = await createMedication({
          lovedOneId,
          name: name.trim(),
          timeSlot: timeSlot as 'morning' | 'afternoon' | 'evening' | 'bedtime',
          specificTime: specificTime.trim(),
          notes: notes.trim() || undefined,
          frequency: frequency as
            | 'daily'
            | 'weekly'
            | 'biweekly'
            | 'monthly_day_of_week'
            | 'monthly_date'
            | 'one_time',
          frequencyDayOfWeek,
          frequencyOrdinal: frequencyOrdinal as '1st' | '2nd' | '3rd' | '4th' | 'last' | undefined,
          frequencyDayOfMonth,
          oneTimeDate: frequency === 'one_time' ? oneTimeDate : undefined,
          remindDayBefore: frequency === 'one_time' ? remindDayBefore : undefined,
        })
        if (!result.success) {
          if (result.error === 'Duplicate medication') {
            setGeneralError(t('medicationManagement.validation.duplicateMedication'))
          } else {
            setGeneralError(result.error || t('errors.generic'))
          }
          return
        }
        showToast({
          message: t('medicationManagement.toast.medicationAdded', { name: name.trim() }),
          type: 'success',
        })
      }
      onClose()
    } catch {
      setGeneralError(t('errors.generic'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const formStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  }

  const errorBannerStyles: React.CSSProperties = {
    padding: '0.75rem 1rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    fontSize: '14px',
  }

  const buttonRowStyles: React.CSSProperties = {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '0.5rem',
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEditMode
          ? t('medicationManagement.editMedication')
          : t('medicationManagement.addMedication')
      }
    >
      <div style={formStyles}>
        {generalError && <div style={errorBannerStyles}>{generalError}</div>}

        <Input
          label={t('medicationManagement.form.name')}
          value={name}
          onChange={e => {
            setName(e.target.value)
            if (nameError) setNameError('')
          }}
          placeholder={t('medicationManagement.form.namePlaceholder')}
          error={nameError}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSubmit()
            }
          }}
        />

        <ScheduleBuilder
          frequency={frequency}
          dayOfWeek={frequencyDayOfWeek}
          ordinal={frequencyOrdinal}
          dayOfMonth={frequencyDayOfMonth}
          oneTimeDate={oneTimeDate}
          remindDayBefore={remindDayBefore}
          onChange={values => {
            setFrequency(values.frequency)
            setFrequencyDayOfWeek(values.dayOfWeek)
            setFrequencyOrdinal(values.ordinal)
            setFrequencyDayOfMonth(values.dayOfMonth)
            setOneTimeDate(values.oneTimeDate)
            setRemindDayBefore(values.remindDayBefore ?? false)
          }}
        />

        <Select
          label={t('medicationManagement.form.timeSlot')}
          value={timeSlot}
          onChange={value => {
            setTimeSlot(value)
            if (timeSlotError) setTimeSlotError('')
            // Pre-populate specific time when empty
            if (!specificTime.trim()) {
              const defaults: Record<string, string> = {
                morning: '8:00 AM',
                afternoon: '1:00 PM',
                evening: '6:00 PM',
                bedtime: '10:00 PM',
              }
              if (defaults[value]) {
                setSpecificTime(defaults[value])
                if (specificTimeError) setSpecificTimeError('')
              }
            }
          }}
          options={timeSlotOptions}
          error={timeSlotError}
        />

        <TimePicker
          label={t('medicationManagement.form.specificTime')}
          value={specificTime}
          onChange={val => {
            setSpecificTime(val)
            if (specificTimeError) setSpecificTimeError('')
          }}
          error={specificTimeError}
        />

        <Input
          label={t('medicationManagement.form.notes')}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={t('medicationManagement.form.notesPlaceholder')}
        />

        <div style={buttonRowStyles}>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} style={{ flex: 1 }}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} style={{ flex: 1 }}>
            {isSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
